<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MetaConnectionAPI from 'dashboard/api/metaConnection';
import { BTN_SECONDARY, BTN_SMALL, CARD, CHIP, CHIP_TONES, FIELD, SMALL, TILE } from '../ui';

// Instagram e Facebook pela conexão que já existe em Integrações (Meta.vue):
// uma Página do Facebook liga os dois.
const { t } = useI18n();

const status = ref({});
const loading = ref(true);
const pageId = ref('');
const saving = ref(false);
const failed = ref(false);

const facebook = computed(() => status.value.facebook || {});
const instagram = computed(() => status.value.instagram || {});
const anyConnected = computed(
  () => facebook.value.connected || instagram.value.connected
);

onMounted(async () => {
  try {
    const { data } = await MetaConnectionAPI.getStatus();
    status.value = data || {};
  } catch {
    status.value = {};
  } finally {
    loading.value = false;
  }
});

const connect = async () => {
  if (!pageId.value.trim()) return;
  saving.value = true;
  failed.value = false;
  try {
    const { data } = await MetaConnectionAPI.connect(pageId.value.trim());
    status.value = data || {};
    pageId.value = '';
  } catch {
    failed.value = true;
  } finally {
    saving.value = false;
  }
};

const rows = computed(() => [
  {
    key: 'ig',
    icon: 'i-ri-instagram-fill',
    tile: 'bg-[linear-gradient(135deg,#F58529,#DD2A7B_55%,#8134AF)]',
    name: instagram.value.connected ? `@${instagram.value.username}` : t('ONBOARDING_VISOR.ACCOUNTS.META.INSTAGRAM'),
    detail: t('ONBOARDING_VISOR.ACCOUNTS.META.INSTAGRAM_DETAIL'),
    connected: instagram.value.connected,
  },
  {
    key: 'fb',
    icon: 'i-ri-facebook-circle-fill',
    tile: 'bg-[#1877F2]',
    name: facebook.value.connected ? facebook.value.page_name || facebook.value.page_id : t('ONBOARDING_VISOR.ACCOUNTS.META.FACEBOOK'),
    detail: t('ONBOARDING_VISOR.ACCOUNTS.META.FACEBOOK_DETAIL'),
    connected: facebook.value.connected,
  },
]);
</script>

<template>
  <section :class="CARD" data-testid="meta-card">
    <div class="flex items-center gap-3">
      <span :class="TILE" class="bg-n-social-ig-tint text-n-social-ig">
        <Icon icon="i-ri-instagram-fill" />
      </span>
      <h2 class="text-xl font-medium tracking-[-0.02em]">
        {{ t('ONBOARDING_VISOR.ACCOUNTS.META.TITLE') }}
      </h2>
    </div>
    <p class="mt-2 mb-4" :class="SMALL">{{ t('ONBOARDING_VISOR.ACCOUNTS.META.ONE_PAGE') }}</p>

    <div
      v-for="row in rows"
      :key="row.key"
      class="flex items-center gap-3 py-3 border-t border-n-weak"
    >
      <span class="size-9 rounded-lg grid place-items-center text-white flex-none" :class="row.tile">
        <Icon :icon="row.icon" class="size-5" />
      </span>
      <div class="min-w-0">
        <b class="font-medium block truncate">{{ row.name }}</b>
        <p :class="SMALL">{{ row.detail }}</p>
      </div>
      <span
        class="ml-auto"
        :class="[CHIP, row.connected ? CHIP_TONES.ok : CHIP_TONES.neutro]"
      >
        <Icon v-if="row.connected" icon="i-ph-check-circle-duotone" class="size-3.5" />
        {{
          row.connected
            ? t('ONBOARDING_VISOR.ACCOUNTS.META.CONNECTED')
            : t('ONBOARDING_VISOR.ACCOUNTS.META.NOT_CONNECTED')
        }}
      </span>
    </div>

    <form v-if="!loading && !anyConnected" class="mt-3 grid gap-2" @submit.prevent="connect">
      <label class="text-[0.8125rem] text-n-slate-11" for="onboarding-page-id">
        {{ t('ONBOARDING_VISOR.ACCOUNTS.META.PAGE_ID') }}
      </label>
      <div class="flex gap-2">
        <input
          id="onboarding-page-id"
          v-model="pageId"
          inputmode="numeric"
          :class="FIELD"
          class="flex-1 min-w-0 !mb-0"
          :placeholder="t('ONBOARDING_VISOR.ACCOUNTS.META.PAGE_ID_PLACEHOLDER')"
        />
        <button type="submit" :class="[BTN_SECONDARY]" :disabled="saving || !pageId.trim()">
          {{ t('ONBOARDING_VISOR.ACCOUNTS.META.CONNECT') }}
        </button>
      </div>
      <p v-if="failed" class="text-sm text-n-amber-11">{{ t('ONBOARDING_VISOR.ACCOUNTS.META.ERROR') }}</p>
    </form>
    <router-link
      v-else-if="!loading"
      :to="{ name: 'settings_integrations_meta' }"
      class="mt-4"
      :class="[BTN_SECONDARY, BTN_SMALL]"
    >
      {{ t('ONBOARDING_VISOR.ACCOUNTS.META.MANAGE') }}
    </router-link>
  </section>
</template>
